/**
 * Channel-modifier schema v7, as the wizard authors it.
 *
 * v7 lets a BEFORE modifier wait for an interlock on a channel other than its
 * target, fail closed (DROP) and optionally undo its payload (REVERT), and lets
 * an INBOUND AFTER modifier fire on state changes the source reports. Every v7
 * field defaults to the pre-v7 meaning, so a modifier only becomes v7 when one
 * of them is actually used — and an older VTS skips a v7 row entirely, which is
 * why the wizard must not mark a modifier v7 that does not need to be.
 *
 * Everything here is pure, so the rules the form enforces are testable without
 * rendering the form.
 */
import * as flatbuffers from "flatbuffers";
import { ActiveState } from "../../../flatbuffers/generated/archive/active-state";
import { SequenceStepScope } from "../../../flatbuffers/generated/archive/sequence-step-scope";
import { ModifierTimeoutPolicy } from "../../../flatbuffers/generated/archive/modifier-timeout-policy";
import { ModifierDropAction } from "../../../flatbuffers/generated/archive/modifier-drop-action";
import { ModifierDirection } from "../../../flatbuffers/generated/archive/modifier-direction";
import { ModifierType } from "../../../flatbuffers/generated/archive/modifier-type";
import { ConflictPolicy } from "../../../flatbuffers/generated/archive/conflict-policy";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import {
  Mapping as SequentMapping,
  verifyRootAsMapping as verifySequentMapping,
} from "../../../flatbuffers/generated/sequent/mapping";
import {
  extractLeafValueFromPayload,
  extractPositionFromPayload,
} from "../../../flatbuffers/builders/channelModifierPayloadBuilder";
import { categoryKeyToMessageContent } from "../../../flatbuffers/types/ln2MessageOptions";
import { unpackModbusTemplate } from "../../../utils/modbusFlatBuffer";
import { uuidToString, ZERO_UUID_STRING } from "../../../utils/uuid";

/**
 * Read an enum field from an API row that may carry the number (the Object-API
 * unpack) or the member name (a JSON path), falling back to the schema default
 * for anything that is neither — so an unexpected shape reads as the pre-v7
 * behaviour rather than as NaN.
 */
export function enumMember<E extends number>(
  enumObj: { [key: string]: string | number },
  raw: unknown,
  fallback: E,
): E {
  if (typeof raw === "number" && typeof enumObj[raw] === "string") return raw as E;
  if (typeof raw === "string" && typeof enumObj[raw] === "number") return enumObj[raw] as E;
  return fallback;
}

/** The schema version of a modifier that uses nothing added in v7. */
export const MODIFIER_SCHEMA_V6 = 6;
/** The schema version of a modifier that uses any v7 field. */
export const MODIFIER_SCHEMA_V7 = 7;

/** The per-step fields v7 added. */
export interface V7StepFields {
  expectedActive: ActiveState;
  scope: SequenceStepScope;
}

/** The modifier-level fields v7 added, plus the steps that carry the step-level ones. */
export interface V7ModifierFields {
  direction: ModifierDirection;
  payloadActive: ActiveState;
  timeoutPolicy: ModifierTimeoutPolicy;
  dropAction: ModifierDropAction;
  /** "" means "the target channel" — the pre-v7 behaviour. */
  observeChannelId: string;
  steps: readonly V7StepFields[];
}

/**
 * The schema version a modifier needs: 7 exactly when it uses a v7 field, else 6.
 *
 * Mirrors LiftCloud's `RequiredSchemaVersion` rule — INBOUND, a payload state
 * other than UNSET, DROP, REVERT, an observe channel, or any step with an
 * expected state or a scope other than SAME_POSITION — so the wizard and the
 * backend agree on which rows an older VTS will skip.
 */
export function requiredSchemaVersion(m: V7ModifierFields): number {
  const usesV7 =
    m.direction === ModifierDirection.INBOUND ||
    m.payloadActive !== ActiveState.UNSET ||
    m.timeoutPolicy === ModifierTimeoutPolicy.DROP ||
    m.dropAction === ModifierDropAction.REVERT ||
    m.observeChannelId !== "" ||
    m.steps.some(
      (s) => s.expectedActive !== ActiveState.UNSET || s.scope !== SequenceStepScope.SAME_POSITION,
    );
  return usesV7 ? MODIFIER_SCHEMA_V7 : MODIFIER_SCHEMA_V6;
}

/** The expected-sequence section applies to BEFORE modifiers and to INBOUND AFTER ones. */
export function isSequenceVisible(type: ModifierType, direction: ModifierDirection): boolean {
  return (
    type === ModifierType.BEFORE ||
    (type === ModifierType.AFTER && direction === ModifierDirection.INBOUND)
  );
}

/** The payload state applies wherever a payload is sent: BEFORE and AFTER. */
export function isPayloadActiveVisible(type: ModifierType): boolean {
  return type === ModifierType.BEFORE || type === ModifierType.AFTER;
}

/**
 * The timeout policy applies to a BEFORE gate that has steps to wait for — the
 * only modifier that holds a message back.
 */
export function isTimeoutPolicyVisible(
  type: ModifierType,
  direction: ModifierDirection,
  stepCount: number,
): boolean {
  return type === ModifierType.BEFORE && direction === ModifierDirection.OUTBOUND && stepCount > 0;
}

/** A picked message leaf: the (content, value) pair every picker produces. */
export interface LeafRef {
  messageContent: number;
  messageValue: number;
}

/** One form step, as the wizard holds it. */
export interface FormStep extends V7StepFields {
  messageRef: LeafRef | null;
  timeoutMs: number;
}

/** The form fields the v7 rules read. */
export interface V7Form {
  modifierType: ModifierType;
  direction: ModifierDirection;
  payloadActive: ActiveState;
  timeoutPolicy: ModifierTimeoutPolicy;
  dropAction: ModifierDropAction;
  observeChannelId: string;
  expectedSequence: readonly FormStep[];
}

/** One expected step as it goes on the wire. */
export interface WireStep extends V7StepFields {
  expectedMessageContent: number;
  expectedMessageValue: number;
  timeoutMs: number;
}

/** The v7 fields as saved, and the schema version they require. */
export interface EffectiveV7Fields extends Omit<V7ModifierFields, "steps"> {
  steps: WireStep[];
  schemaVersion: number;
}

/**
 * What the form actually saves for the v7 fields.
 *
 * A field is only saved while the control that sets it is shown: hidden steps,
 * a DROP left over from a step list since emptied, or an observe channel on a
 * modifier with no sequence section would otherwise still reach the wire — and
 * silently make an ordinary modifier v7, which an older VTS skips. Steps left
 * without a message are dropped, as they always were.
 */
export function effectiveV7Fields(form: V7Form): EffectiveV7Fields {
  const sequenceVisible = isSequenceVisible(form.modifierType, form.direction);
  const steps: WireStep[] = sequenceVisible
    ? form.expectedSequence
        .filter((s): s is FormStep & { messageRef: LeafRef } => s.messageRef !== null)
        .map((s) => ({
          expectedMessageContent: s.messageRef.messageContent,
          expectedMessageValue: s.messageRef.messageValue,
          timeoutMs: s.timeoutMs,
          expectedActive: s.expectedActive,
          scope: s.scope,
        }))
    : [];
  const timeoutPolicy = isTimeoutPolicyVisible(form.modifierType, form.direction, steps.length)
    ? form.timeoutPolicy
    : ModifierTimeoutPolicy.RELEASE;
  const fields = {
    direction: form.direction,
    payloadActive: isPayloadActiveVisible(form.modifierType)
      ? form.payloadActive
      : ActiveState.UNSET,
    timeoutPolicy,
    dropAction:
      timeoutPolicy === ModifierTimeoutPolicy.DROP ? form.dropAction : ModifierDropAction.NONE,
    observeChannelId: sequenceVisible ? form.observeChannelId : "",
    steps,
  };
  return { ...fields, schemaVersion: requiredSchemaVersion(fields) };
}

/** The form fields a direction change touches. */
export interface DirectionFields {
  direction: ModifierDirection;
  modifierType: ModifierType;
  timeoutPolicy: ModifierTimeoutPolicy;
  dropAction: ModifierDropAction;
  conflictPolicy: ConflictPolicy;
}

/**
 * Switch direction. INBOUND forces AFTER — an inbound modifier reacts to a
 * state change the source already reported, so there is nothing to hold back —
 * and so also clears the options only a held message has: DROP, REVERT and the
 * QUEUE conflict policy.
 */
export function withDirection<T extends DirectionFields>(form: T, direction: ModifierDirection): T {
  if (direction !== ModifierDirection.INBOUND) return { ...form, direction };
  return {
    ...form,
    direction,
    modifierType: ModifierType.AFTER,
    timeoutPolicy: ModifierTimeoutPolicy.RELEASE,
    dropAction: ModifierDropAction.NONE,
    conflictPolicy:
      form.conflictPolicy === ConflictPolicy.QUEUE ? ConflictPolicy.COEXIST : form.conflictPolicy,
  };
}

/** A channel the observe-channel dropdown can offer. */
export interface ObservableChannel {
  id: string;
  name: string;
  serverId: string;
}

/**
 * The observe-channel choices: "Target channel" (the default, stored as ""),
 * then every channel on the source's server — minus the source itself for a
 * BEFORE modifier, whose source loop is parked while its gate waits and so
 * could never deliver the confirmation.
 */
export function observeChannelOptions(
  channels: readonly ObservableChannel[],
  sourceChannelId: string,
  modifierType: ModifierType,
): { label: string; value: string }[] {
  const source = channels.find((c) => c.id === sourceChannelId);
  const sameServer = source
    ? channels.filter(
        (c) =>
          c.serverId === source.serverId &&
          !(modifierType === ModifierType.BEFORE && c.id === sourceChannelId),
      )
    : [];
  return [
    { label: "Target channel", value: "" },
    ...sameServer.map((c) => ({ label: c.name, value: c.id })),
  ];
}

/**
 * The scope a new step starts with. On a multi-car bank a floor alone does not
 * say which car's device answered, so the default also pins the lift.
 */
export function defaultStepScope(maxLiftsOnSourceChannel: number): SequenceStepScope {
  return maxLiftsOnSourceChannel > 1
    ? SequenceStepScope.SAME_POSITION_AND_LIFT
    : SequenceStepScope.SAME_POSITION;
}

/**
 * Which of the modifier's own messages a step waits for, if any.
 *
 * Not an error. VTS never observes the source command or its own payload --
 * both go straight to a vendor -- and a step counts only a change the observe
 * channel reports after the payload write starts. So a device that confirms by
 * reporting the very state it was commanded (send CarCallLock inactive, wait
 * for CarCallLock inactive) is a legitimate confirmation. The wizard notes the
 * reuse so it is a choice, not an accident.
 */
export function stepLeafCollision(
  step: LeafRef | null,
  source: LeafRef | null,
  payload: LeafRef | null,
): "source" | "payload" | null {
  if (step === null) return null;
  const same = (other: LeafRef | null) =>
    other !== null &&
    other.messageContent === step.messageContent &&
    other.messageValue === step.messageValue;
  if (same(source)) return "source";
  if (same(payload)) return "payload";
  return null;
}

/** The picker key for a leaf: `"<messageContent>:<messageValue>"`. */
export function leafKey(messageContent: number, messageValue: number): string {
  return `${messageContent}:${messageValue}`;
}

/**
 * One place an adapter maps a leaf. `position` and `liftId` are known only
 * where the mapping names them (a Sequent pin does); elsewhere they are null
 * and "", which the ambiguity check treats as "could be any".
 */
export interface LeafMapping {
  position: number | null;
  liftId: string;
}

/** Every mapping of every leaf an adapter knows, keyed by `leafKey`. */
export type LeafMappings = Map<string, LeafMapping[]>;

/** Add one mapping to a `LeafMappings`, mutating it (builders only). */
export function addLeafMapping(into: LeafMappings, key: string, mapping: LeafMapping): void {
  const bucket = into.get(key);
  if (bucket) bucket.push(mapping);
  else into.set(key, [mapping]);
}

/**
 * The leaf mappings of a Sequent adapter's binary CDO, or null when the bytes
 * are not a Sequent `Mapping`.
 *
 * Decoded through the generated Object API, after the generated verifier has
 * accepted the buffer — a buffer of another schema must be refused, not read as
 * garbage. Each pin's LN2 message gives the leaf (via the same extractor the
 * modifier table uses), the floor it reports, and the lift it belongs to.
 */
export function sequentLeafMappings(bytes: Uint8Array): LeafMappings | null {
  try {
    verifySequentMapping(new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength));
    const mapping = SequentMapping.getRootAsMapping(new flatbuffers.ByteBuffer(bytes)).unpack();
    const out: LeafMappings = new Map();
    mapping.discreteToMessage.forEach((entry) => {
      const msg = entry.value?.msg ?? null;
      const leaf = extractLeafValueFromPayload(msg);
      if (leaf === null) return;
      const liftUuid = entry.value?.liftId?.id ?? null;
      const liftId = liftUuid ? uuidToString(liftUuid) : "";
      addLeafMapping(out, leafKey(leaf.messageContent, leaf.messageValue), {
        position: extractPositionFromPayload(msg),
        liftId: liftId === ZERO_UUID_STRING ? "" : liftId,
      });
    });
    return out;
  } catch {
    return null;
  }
}

/** What an adapter's binary CDO says it maps: the picker filter, and every mapping. */
export interface AdapterLeafIndex {
  /** `leafKey`s for the message pickers' filter — reads and writes alike. */
  filterKeys: Set<string>;
  /** Mappings a confirmation could be observed through, for the A6 warning. */
  mappings: LeafMappings;
}

/**
 * Index a Modbus CDO template, or null when the bytes do not unpack as one.
 *
 * The filter takes reads AND writes (the pickers offer both, as before); the
 * mappings take reads only, because a write never produces an observation.
 * Template entries carry no floor, so every mapping is positionless.
 */
function modbusLeafIndex(bytes: Uint8Array): AdapterLeafIndex | null {
  try {
    const template = unpackModbusTemplate(bytes);
    const filterKeys = new Set<string>();
    const mappings: LeafMappings = new Map();
    const keyOf = (msgId: string | null | undefined): string | null => {
      const parts = (msgId ?? "").split(":");
      if (parts.length < 3) return null;
      const mc = categoryKeyToMessageContent(parts[0]!);
      const mv = Number(parts[2]);
      if (mc === MessageContent.NONE || !Number.isFinite(mv)) return null;
      return leafKey(mc, mv);
    };
    template.reads.forEach((r) => {
      const key = keyOf(r.msgId);
      if (key === null) return;
      filterKeys.add(key);
      addLeafMapping(mappings, key, { position: null, liftId: "" });
    });
    template.writes.forEach((w) => {
      const key = keyOf(w.msgId);
      if (key !== null) filterKeys.add(key);
    });
    return { filterKeys, mappings };
  } catch {
    return null;
  }
}

/**
 * Index an adapter's binary CDO, whichever schema it is in.
 *
 * Modbus first, as before; when that fails or maps nothing, the bytes are tried
 * as a Sequent `Mapping` — the adapter a car-call lock's confirmation input is
 * typically wired to. Anything else indexes as empty, so the caller caches it
 * and does not refetch.
 */
export function cdoLeafIndex(bytes: Uint8Array): AdapterLeafIndex {
  const modbus = modbusLeafIndex(bytes);
  if (modbus !== null && modbus.filterKeys.size > 0) return modbus;
  const sequent = sequentLeafMappings(bytes);
  if (sequent !== null) return { filterKeys: new Set(sequent.keys()), mappings: sequent };
  return modbus ?? { filterKeys: new Set(), mappings: new Map() };
}

/**
 * True when an observed confirmation could come from more than one of these
 * mappings under the step's scope — several locks sharing one leaf.
 *
 * SAME_POSITION compares floors only when both sides carry one, so a mapping
 * without a position matches any floor; SAME_POSITION_AND_LIFT also tells lifts
 * apart; ANY_POSITION tells nothing apart.
 */
export function mappingsAmbiguous(
  mappings: readonly LeafMapping[],
  scope: SequenceStepScope,
): boolean {
  if (mappings.length < 2) return false;
  if (scope === SequenceStepScope.ANY_POSITION) return true;
  const withLift = scope === SequenceStepScope.SAME_POSITION_AND_LIFT;
  return mappings.some((m, i) =>
    mappings.some((other, j) => {
      if (i === j) return false;
      // Two known, different lifts are told apart by SAME_POSITION_AND_LIFT.
      if (withLift && m.liftId && other.liftId && m.liftId !== other.liftId) return false;
      // Otherwise the floor decides — and a missing floor matches any floor.
      return m.position === null || other.position === null || m.position === other.position;
    }),
  );
}
